import {MDIcon} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {BaseComponentWithContext} from '@mintdata/mint-kit/api/components/BaseComponentWithContext';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {DataSource, IList, IListItem, ListItem, ListModel} from '@mintdata/mint-kit/api/models/List';
import {ListItemRenderer, ListView,} from '@mintdata/mint-kit/impl/components/list/ListView';
import {Popover} from '@mintdata/mint-kit/impl/components/popover/Popover';
import {PopupAnchor, PopupSize, SourceAnchor} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {IStyles, Styles} from '@mintdata/mint-kit/impl/util/Styles';
import * as classnames from 'classnames/bind';
import * as React from 'react';

const comboStyles = require('@mintdata-wsp/common/components/combo/Combo.less');
const cx = classnames.bind(comboStyles);

export interface IComboState
{
    isOpened:boolean;
}

export type ComboContentRenderer<T> = (data:T, state:IComboState, styles:IStyles) => JSX.Element;
export type ComboSelectItemHandler<T> = (item:T) => void;

export interface IComboProps<T>
{
    className?:string;
    dataSource?:DataSource<T>;
    theme?:IStyles;
    itemRenderer?:ListItemRenderer<T>;
    contentRenderer?:ComboContentRenderer<T>;
    onSelectItem?:ComboSelectItemHandler<T>;
    sourceAnchor?:SourceAnchor;
    popoverAnchor?:PopupAnchor;
    popupSize?:PopupSize;
}

export {SourceAnchor, PopupAnchor};

export interface IComboComponentState<T> extends IComboState
{
    listModel:IList<T> | null;
    selectedItem:IListItem<T>;
    styles:IStyles;
}

enum Actions
{
    ClosePopover = 1,
    TogglePopover = 2,
    SelectListItem = 3
}

export class Combo<T> extends BaseComponentWithContext<IComboProps<T>, IComboComponentState<T>>
{
    private readonly _id:GUID = IdUtil.newGUID();

    public constructor(props:any)
    {
        super(props);

        const state = this.state = {
            isOpened: false,
            listModel: null,
            selectedItem: null,
            styles: null
        };

        this._selectInitialItem(props, state);
    }

    public componentWillReceiveProps(newProps:IComboProps<T>):void
    {
        const {dataSource, theme} = this.props;

        if( dataSource !== newProps.dataSource )
            this._initDataSource(newProps.dataSource);

        this._selectInitialItem(newProps, null);

        if( theme !== newProps.theme )
            this._initStyles(newProps.theme);
    }

    public componentWillMount():void
    {
        const {dataSource, theme} = this.props;
        this._initDataSource(dataSource);
        this._initStyles(theme);
    }

    private _selectInitialItem(props:IComboProps<T>, state:IComboComponentState<T> | null = null):void
    {
        if( props.dataSource )
        {
            let getSelectedItemsFn = (props.dataSource as IList<any>).getSelectedItems;
            if( getSelectedItemsFn && typeof getSelectedItemsFn === 'function' )
            {
                let selectedItems = getSelectedItemsFn.call(props.dataSource);
                if( selectedItems && selectedItems.length > 0 )
                {
                    const firstItem = selectedItems[0];

                    if( state )
                        state.selectedItem = firstItem;
                    else
                        this.setState({selectedItem: firstItem});
                }
            }
        }
    }

    private _selectListItem(listItem:IListItem<T>):void
    {
        this._closePopover(listItem);
        this.setState({selectedItem: listItem} as IComboComponentState<T>);

        const {onSelectItem} = this.props;
        if( listItem && onSelectItem )
            onSelectItem(listItem.getData());
    }

    public static renderSelectContent(data:any, state:IComboState, styles:IStyles):JSX.Element
    {
        const {label} = data;
        const {isOpened} = state;
        const cx = classnames.bind(styles);

        return (
            <div className={styles.select}>
                {label}
                <MDIcon name={MDIcons.common.ArrowDropdown} className={cx('icon', {reverseIcon: isOpened})} />
            </div>
        );
    }

    private _actionHandler(action:Actions, listItem?:ListItem<T>)
    {
        switch( action )
        {
            case Actions.SelectListItem:
                this._selectListItem(listItem);
                break;

            case Actions.ClosePopover:
                this._closePopover(null);
                break;

            case Actions.TogglePopover:
                this.setState({isOpened: !this.state.isOpened});
                break;
        }
    }

    private _getHeaderFooterTemplates()
    {
        const {children} = this.props;

        const templates = {header: null as any, footer: null as any};

        if( children )
        {
            React.Children.forEach(children, (child:React.ReactElement<any>) =>
            {
                if( child.type === 'header' )
                    templates.header = child;

                if( child.type === 'footer' )
                    templates.footer = child;
            });
        }

        return templates;
    }

    private _closePopover(selectedItem:IListItem<any> | null):void
    {
        if( selectedItem )
            this.setState({isOpened: false, selectedItem: selectedItem});
        else
            this.setState({isOpened: false});
    }

    private _renderSelect()
    {
        const {contentRenderer = Combo.renderSelectContent} = this.props;
        const {isOpened, selectedItem, styles} = this.state;
        const data = selectedItem ? selectedItem.getData() : {} as T;
        const itemState:IComboState = {isOpened};

        return (
            <div
                className={styles.selectContainer}
                onClick={() => this._actionHandler(Actions.TogglePopover)}
            >
                {contentRenderer(data, itemState, styles)}
            </div>
        );
    }

    private _renderPopover()
    {
        const templates = this._getHeaderFooterTemplates();
        const {itemRenderer} = this.props;
        const {listModel, styles} = this.state;

        return (
            <Popover
                id={this._id}
                onDismissed={() => this._actionHandler(Actions.ClosePopover)}
                isModal={false}
            >
                <div className={styles.content}>
                    {templates.header}
                    <ListView
                        className={styles.listView}
                        dataSource={listModel}
                        itemRenderer={itemRenderer}
                        onItemSelect={(item:ListItem<T>) => this._actionHandler(Actions.SelectListItem, item)}
                        theme={styles}
                    />
                    {templates.footer}
                </div>
            </Popover>
        );
    }

    private _initDataSource(dataSource:DataSource<T>):void
    {
        let ds = dataSource instanceof ListModel
            ? dataSource as any
            : new ListModel<T>(dataSource as Array<T>);

        this.setState({
            listModel: ds,
        });
    }

    private _initStyles(styles:IStyles):void
    {
        this.setState({
            styles: Styles.unionStyles(styles, comboStyles)
        });
    }

    public render():JSX.Element
    {
        const {popupManager} = this.context;
        const {className, sourceAnchor, popoverAnchor, popupSize = PopupSize.WidthOfSource} = this.props;
        const {isOpened, styles} = this.state;

        return (
            <div
                className={cx('combo', className)}
                ref={popupManager.position(
                    this._id,
                    sourceAnchor,
                    popoverAnchor,
                    popupSize
                )}
            >
                {this._renderSelect()}
                {isOpened && this._renderPopover()}
            </div>
        );
    }
}

export type ComboConstructor<T> = new() => Combo<T>;
